import { ReactNode, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { Loader2 } from "lucide-react";

interface EmployeeProtectedRouteProps {
  children: ReactNode;
}

const REMOVED_EMPLOYEE_TYPES = ["co_admin", "chatter", "supervisor_chatter"];

export function EmployeeProtectedRoute({ children }: EmployeeProtectedRouteProps) {
  const { user, userRole, userRoleData, isLoading } = useAuth();

  const isRemovedRole =
    userRole === "employee" &&
    userRoleData?.employee_type &&
    REMOVED_EMPLOYEE_TYPES.includes(userRoleData.employee_type);

  useEffect(() => {
    if (isRemovedRole) {
      supabase.auth.signOut();
    }
  }, [isRemovedRole]);

  // Wait for both auth and role to load before making decisions
  if (isLoading || (user && userRole === null)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (isRemovedRole) {
    return <Navigate to="/auth" replace />;
  }

  // Allow both employees and admins
  if (userRole !== "employee" && userRole !== "admin") {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

